import React, { useEffect, useState } from "react";
import {
   ECLDateTimePicker,
   ECLSelect,
   ECLNumber,
   ECLThemeImage,
   ECLDropDown,
} from "../../../";
import { translate } from "../../../../utilities/Translate";
import debounce from "../../../../utilities/debounce";
import useSelectorId from "../../../../utilities/hooks/useSelectorId";
import "../ECLSearch.css";

export default function ECLSearchInputs(props) {
   const inputInstance = useSelectorId();
   //going to use open/setOpen just to track dropdown state to ensure we can
   //keep our event listeners in sync
   const [open, setOpen] = useState(true);
   //Debounce the signal that let's us know when someone has submitted something into input, but hasn't touched it in a couple seconds
   const signalInput = debounce(() => {
      const formElement = document.querySelector(
         `#search-input-${inputInstance}-${props.id}`,
      ); //get form element
      formElement.dispatchEvent(new CustomEvent("submitSignal")); //dispatch ready for submit event
   }, 800);

   // Focus on the relation that was "focused" and scroll to it
   useEffect(() => {
      const activeElement = document.getElementById(
         `search-input-${inputInstance}-${props.id}`,
      );
      if (activeElement) {
         activeElement.scrollIntoView({
            behavior: "instant",
            block: "nearest",
            inline: "start",
         });
      }
      const formElement = document.querySelector(
         `#search-input-${inputInstance}-${props.id}`,
      );
      if (formElement) {
         formElement.addEventListener("input", signalInput);
         formElement.addEventListener("submitSignal", handleSignal);
      }
      return () => {
         if (formElement) {
            formElement.removeEventListener("input", signalInput);
            formElement.removeEventListener("submitSignal", handleSignal);
         }
      };
   }, [open, props.searchable]); //These should track everything in the handle signal function!

   //When "SubmitSignal" event occurs, do the thing with the current state
   function handleSignal() {
      let key_vals = [];
      if (props.searchable.type === "text") {
         key_vals = [
            {
               key: props.searchable.name,
               value: {
                  filter: {
                     selections: props.searchable.selections
                        .filter((s) => s.selected)
                        .map((s) => s.searchableString),
                  },
               },
            },
         ];
      } else if (props.searchable.type === "timestamp") {
         key_vals = [
            {
               key: props.searchable.name,
               value: {
                  filter: {
                     datetime_min: document.querySelector(
                        `#datetime-min-params-${inputInstance}-${props.id}`,
                     ).value,
                     datetime_max: document.querySelector(
                        `#datetime-max-params-${inputInstance}-${props.id}`,
                     ).value,
                  },
               },
            },
         ];
      } else {
         key_vals = [
            {
               key: props.searchable.name,
               value: {
                  filter: {
                     min: Number(
                        document.querySelector(
                           `#number-min-params-${inputInstance}-${props.id}`,
                        ).value,
                     ),
                     max: Number(
                        document.querySelector(
                           `#number-max-params-${inputInstance}-${props.id}`,
                        ).value,
                     ),
                  },
               },
            },
         ];
      }

      props.updateState(key_vals);
   }

   function resetSearchable() {
      props.updateState([
         { key: props.searchable.name, value: { remove_filter: true } },
      ]);
      props.removeSearchable(props.searchable);
   }

   let input = null;
   switch (props.searchable.type) {
      case "text":
         input = (
            <ECLSelect
               options={props.searchable.selections}
               setOptions={(selections) => {
                  props.setSearchableSelections(selections);
               }}
               multiselect={true}
               filter={true}
               menuWidth={300}
               inModal={true}
               startOpen={props.searchable.open}
               customTrigger={
                  <div className="search-select-combo">
                     <label>{props.searchable.name}</label>
                     <p>
                        {props.searchable.selections
                           .filter((s) => s.selected)
                           .slice(0, 10)
                           .map((s) => s.searchableString)
                           .join(", ")}
                     </p>
                  </div>
               }
            />
         );
         break;
      case "timestamp":
         input = (
            <>
               <ECLDateTimePicker
                  id={`datetime-min-params-${inputInstance}-${props.id}`}
                  min={props.searchable.min}
                  max={props.searchable.max}
                  date={
                     props.searchable.currentMin
                        ? props.searchable.currentMin
                        : props.searchable.min
                  }
               />
               <ECLDateTimePicker
                  id={`datetime-max-params-${inputInstance}-${props.id}`}
                  min={props.searchable.min}
                  max={props.searchable.max}
                  date={
                     props.searchable.currentMax
                        ? props.searchable.currentMax
                        : props.searchable.max
                  }
               />
            </>
         );
         break;
      case "number":
         input = (
            <>
               <ECLNumber
                  id={`number-min-params-${inputInstance}-${props.id}`}
                  min={props.searchable.min}
                  max={props.searchable.max}
                  precision={
                     props.searchable.precision ? props.searchable.precision : 2
                  }
                  value={
                     props.searchable.currentMin
                        ? props.searchable.currentMin
                        : props.searchable.min
                  }
               />
               <ECLNumber
                  id={`number-max-params-${inputInstance}-${props.id}`}
                  min={props.searchable.min}
                  max={props.searchable.max}
                  precision={
                     props.searchable.precision ? props.searchable.precision : 2
                  }
                  value={
                     props.searchable.currentMax
                        ? props.searchable.currentMax
                        : props.searchable.max
                  }
               />
            </>
         );
         break;
      default:
         break;
   }

   if (props.searchable.type === "text") {
      return (
         <div className="search-select-container">
            <form
               className={"search-input-form"}
               id={`search-input-${inputInstance}-${props.id}`}
            >
               {input}
            </form>
            <button onClick={resetSearchable}>
               <ECLThemeImage
                  className="search-xmark"
                  fontAwesome="xmark"
                  alt={translate("Clear Field")}
               />
            </button>
         </div>
      );
   }

   return (
      <>
         <ECLDropDown
            title={translate(`Search by `) + props.searchable.name}
            trigger={props.searchable.name}
            containerId={"search-container-" + props.id}
            menuWidth={250}
            onOpen={() => {
               setOpen(!open);
            }}
            startOpen={props.searchable.open}
         >
            <form
               className={"search-input-form"}
               id={`search-input-${inputInstance}-${props.id}`}
            >
               {input}
            </form>
         </ECLDropDown>
         <span className="search-input-form-preview">
            {props.searchable.type === "timestamp"
               ? new Date(props.searchable.currentMin).toLocaleDateString()
               : props.searchable.currentMin}
            -
            {props.searchable.type === "timestamp"
               ? new Date(props.searchable.currentMax).toLocaleDateString()
               : props.searchable.currentMax}
         </span>

         <button onClick={resetSearchable}>
            <ECLThemeImage
               className="search-xmark"
               fontAwesome="xmark"
               alt={translate("Clear Field")}
            />
         </button>
      </>
   );
}
